import "@/i18n";
import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ArrowLeft, ArrowRight, Check, Loader2, Users, Briefcase, Calendar, Clock, Plane, MessageCircle, CreditCard, Sparkles, MapPin } from "lucide-react";
import { storageKey, type StoredBooking } from "./booking.status.$id";

import { computeRoute, type PlaceSuggestion } from "@/lib/distance.functions";
import { AddressInput } from "@/components/AddressInput";
import { RouteMap } from "@/components/RouteMap";

import { SearchingOverlay } from "@/components/SearchingOverlay";
import { PhoneField } from "@/components/PhoneField";
import logoAsset from "@/assets/logo.png.asset.json";
const logo = logoAsset.url;
import { VEHICLES, BOOKABLE_VEHICLE_IDS, estimatePrice, formatMAD, type VehicleType } from "@/lib/pricing";
import { useFormatPrice } from "@/lib/currency";
import { supabase } from "@/integrations/supabase/client";
import { buildWhatsAppUrl, isWhatsAppConfigured } from "@/lib/whatsapp";
import { useAuth } from "@/hooks/useAuth";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/booking")({
  head: () => ({
    meta: [
      { title: "Book a Private Transfer in Morocco | Instant Quote" },
      { name: "description", content: "Book a private airport transfer in Morocco in 4 steps. Instant fixed-price quote, vetted driver, flight tracking, WhatsApp confirmation." },
      { property: "og:title", content: "Book a Private Transfer in Morocco" },
      { property: "og:description", content: "Instant fixed-price quote in 4 steps. Vetted driver, flight tracking, WhatsApp confirmation." },
      { property: "og:url", content: "https://bachawaytravel.com/booking" },
    ],
    links: [{ rel: "canonical", href: "https://bachawaytravel.com/booking" }],
  }),
  component: BookingPage,
});

const detailsSchema = z.object({
  customer_name: z.string().trim().min(2, "Please enter your full name").max(100),
  customer_email: z.string().trim().email("Invalid email").max(255),
  customer_phone: z.string().trim().min(6, "Invalid phone").max(30),
  flight_number: z.string().trim().max(20).optional(),
  notes: z.string().trim().max(500).optional(),
});

function BookingPage() {
  const { t } = useTranslation();
  const tr = t;
  const fmt = useFormatPrice();
  const { user, isAuthenticated } = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const computeRouteFn = useServerFn(computeRoute);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Scroll to top whenever the step changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [step]);

  // Step 1
  const [tripType, setTripType] = useState<"one-way" | "round-trip">("one-way");
  const [pickup, setPickup] = useState<PlaceSuggestion | null>(null);
  const [dropoff, setDropoff] = useState<PlaceSuggestion | null>(null);
  const [travelDate, setTravelDate] = useState<string>("");
  const [travelTime, setTravelTime] = useState<string>("12:00");
  const [passengers, setPassengers] = useState(1);
  const [luggage, setLuggage] = useState(0);
  const [babySeat, setBabySeat] = useState(false);
  const [surfBoard, setSurfBoard] = useState(false);

  const [routeLoading, setRouteLoading] = useState(false);
  const [route, setRoute] = useState<{ distanceKm: number; durationMin: number; estimated?: boolean; polyline?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "online">("cash");

  // Step 2
  const [vehicleType, setVehicleType] = useState<VehicleType>("economy");

  // Step 3
  const [details, setDetails] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    flight_number: "",
    notes: "",
  });
  const [detailsErrors, setDetailsErrors] = useState<Record<string, string>>({});

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const price = useMemo(() => {
    if (!route) return null;
    return estimatePrice({
      vehicleType,
      distanceKm: route.distanceKm,
      tripType,
      timeHHmm: travelTime,
      memberDiscount: isAuthenticated,
    });
  }, [route, vehicleType, tripType, travelTime, isAuthenticated]);

  // Auto-fetch route as soon as both pickup & dropoff are picked.
  // Invalidate cached route whenever either endpoint changes.
  const lastRouteKey = useRef<string>("");
  useEffect(() => {
    if (!pickup || !dropoff) {
      if (route) setRoute(null);
      lastRouteKey.current = "";
      return;
    }
    const key = `${pickup.lat},${pickup.lng}->${dropoff.lat},${dropoff.lng}`;
    if (key === lastRouteKey.current) return;
    lastRouteKey.current = key;

    let cancelled = false;
    setRouteLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await computeRouteFn({ data: {
          pickupLat: pickup.lat, pickupLng: pickup.lng,
          dropoffLat: dropoff.lat, dropoffLng: dropoff.lng,
        }});
        if (!cancelled) setRoute(r);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not compute distance.");
      } finally {
        if (!cancelled) setRouteLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng]);

  const [transitioning, setTransitioning] = useState(false);

  async function goToStep2() {
    setError(null);
    if (!pickup || !dropoff) { setError(t("bookingForm.errSelect")); return; }
    if (!travelDate || !travelTime) { setError(t("bookingForm.errDate")); return; }
    // Make sure we have a route, but no overlay on this transition.
    if (!route) {
      setRouteLoading(true);
      try {
        const r = await computeRouteFn({ data: {
          pickupLat: pickup.lat, pickupLng: pickup.lng,
          dropoffLat: dropoff.lat, dropoffLng: dropoff.lng,
        }});
        setRoute(r);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Could not compute distance. Try again.");
        setRouteLoading(false);
        return;
      } finally {
        setRouteLoading(false);
      }
    }
    // Show the searching overlay only between trip details and vehicle selection.
    setTransitioning(true);
    await new Promise((r) => setTimeout(r, 3500));
    setTransitioning(false);
    setStep(2);
  }


  async function goToStep3() {
    if (passengers > VEHICLES[vehicleType].capacity) {
      setError(`The ${VEHICLES[vehicleType].name} fits up to ${VEHICLES[vehicleType].capacity} passengers.`);
      return;
    }
    setError(null);
    setStep(3);
  }

  function goToStep4() {
    const parsed = detailsSchema.safeParse(details);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      parsed.error.issues.forEach((i) => { errs[i.path[0] as string] = i.message; });
      setDetailsErrors(errs);
      return;
    }
    setDetailsErrors({});
    setStep(4);
  }

  async function submit() {
    if (!pickup || !dropoff || !route || !price) return;
    setSubmitting(true);
    setError(null);
    try {
      // Generate the id client-side so we don't need PostgREST to return
      // the inserted row. Returning the row would require a SELECT policy
      // for anon, which we deliberately don't have (only admins read bookings).
      const bookingId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

      const { error } = await supabase.from("bookings").insert({
        id: bookingId,
        user_id: user?.id ?? null,
        trip_type: tripType,
        pickup: pickup.label, dropoff: dropoff.label,
        pickup_lat: pickup.lat, pickup_lng: pickup.lng,
        dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
        distance_km: route.distanceKm, duration_min: route.durationMin,
        travel_date: travelDate, travel_time: travelTime,
        passengers, luggage,
        vehicle_type: vehicleType, vehicle_class: "standard",
        customer_name: details.customer_name,
        customer_email: details.customer_email,
        customer_phone: details.customer_phone,
        flight_number: details.flight_number || null,
        notes: [
          details.notes,
          babySeat ? "Extra: Baby seat (free)" : "",
          surfBoard ? "Extra: Surf board (free)" : "",
          isAuthenticated ? "Member discount applied (-10%)" : "",
        ].filter(Boolean).join(" · ") || null,
        price_mad: price.total,
      });

      if (error) throw error;

      const ref = bookingId.slice(0, 8).toUpperCase();
      const msg = [
        `Hello Bacha Way Travel — new booking request (#${ref})`,
        ``,
        `Name: ${details.customer_name}`,
        `Phone: ${details.customer_phone}`,
        `Email: ${details.customer_email}`,
        `Trip: ${tripType === "round-trip" ? t("bookingForm.roundTrip") : t("bookingForm.oneWay")}`,
        `From: ${pickup.label}`,
        `To: ${dropoff.label}`,
        `Date: ${travelDate} at ${travelTime}`,
        `Vehicle: ${VEHICLES[vehicleType].name}`,
        `Passengers: ${passengers} · Luggage: ${luggage}`,
        (babySeat || surfBoard) ? `Extras (free): ${[babySeat && "Baby seat", surfBoard && "Surf board"].filter(Boolean).join(", ")}` : "",
        details.flight_number ? `Flight: ${details.flight_number}` : "",
        details.notes ? `Notes: ${details.notes}` : "",
        `Distance: ${route.distanceKm} km · Estimate: ${formatMAD(price.total)}`,
      ].filter(Boolean).join("\n");

      const waUrl = isWhatsAppConfigured() ? buildWhatsAppUrl(msg) : undefined;

      // Persist booking details so the status page can render them
      // (RLS prevents anonymous re-reads of the bookings table).
      if (typeof window !== "undefined" && bookingId) {
        const stored: StoredBooking = {
          ref,
          method: paymentMethod,
          trip_type: tripType,
          pickup: { label: pickup.label, lat: pickup.lat, lng: pickup.lng },
          dropoff: { label: dropoff.label, lat: dropoff.lat, lng: dropoff.lng },
          distance_km: route.distanceKm,
          duration_min: route.durationMin,
          polyline: route.polyline,
          travel_date: travelDate,
          travel_time: travelTime,
          passengers,
          luggage,
          vehicle_name: VEHICLES[vehicleType].name,
          customer_name: details.customer_name,
          customer_email: details.customer_email,
          customer_phone: details.customer_phone,
          flight_number: details.flight_number || null,
          notes: [
            details.notes,
            babySeat ? "Extra: Baby seat (free)" : "",
            surfBoard ? "Extra: Surf board (free)" : "",
          ].filter(Boolean).join(" · ") || null,
          price_mad: price.total,
          waMessage: msg,
        };
        try {
          sessionStorage.setItem(storageKey(bookingId), JSON.stringify(stored));
        } catch {
          // ignore quota errors
        }
      }

      setConfirming(true);

      // Trigger customer confirmation + admin notification emails without blocking
      // the customer from reaching their confirmed booking page.
      if (details.customer_email) {
        const emailPayload = JSON.stringify({
          recipientEmail: details.customer_email,
          idempotencyKey: `booking-confirm-${bookingId}`,
          templateData: {
            customer_name: details.customer_name,
            customer_email: details.customer_email,
            customer_phone: details.customer_phone,
            reference: ref,
            trip_type: tripType,
            pickup: pickup.label,
            dropoff: dropoff.label,
            travel_date: travelDate,
            travel_time: travelTime,
            vehicle_name: VEHICLES[vehicleType].name,
            passengers,
            luggage,
            distance_km: route.distanceKm,
            price_mad: price.total,
            flight_number: details.flight_number || null,
            notes: details.notes || null,
            payment_method: paymentMethod,
          },
        });
        void fetch("/api/public/booking-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: emailPayload,
          keepalive: true,
        }).catch((emailError) => {
          console.error("Booking confirmation email queue failed", emailError);
        });
      }

      // Keep the "almost there" screen visible, then force a direct move to
      // the final thank-you page so the customer cannot get stuck here.
      await new Promise((r) => setTimeout(r, 4000));
      const statusPath = `/booking/status/${bookingId}`;
      window.location.replace(statusPath);

    } catch (e: unknown) {
      setConfirming(false);
      setError(e instanceof Error ? e.message : "Could not save your booking. Please try again or contact us via WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  }

  if (pathname.startsWith("/booking/status/")) {
    return <Outlet />;
  }

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8 md:py-16">
      {transitioning && <SearchingOverlay />}
      {confirming && <ConfirmingOverlay />}
      <div className="text-xs uppercase tracking-[0.2em] text-primary">{t("bookingForm.title")}</div>
      <h1 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl">
        {step === 1 && t("bookingForm.h1Where")}
        {step === 2 && t("bookingForm.h1Vehicle")}
        {step === 3 && t("bookingForm.h1Details")}
        {step === 4 && t("bookingForm.h1Confirm")}
      </h1>

      {/* Stepper */}
      <ol className="mt-6 sm:mt-8 flex items-center gap-1.5 sm:gap-3 text-[11px] sm:text-xs overflow-x-auto -mx-1 px-1 pb-1">
        {[t("bookingForm.sTrip"), t("bookingForm.sVehicle"), t("bookingForm.sDetails"), t("bookingForm.sConfirm")].map((label, i) => {
          const n = (i + 1) as 1 | 2 | 3 | 4;
          const active = step === n;
          const done = step > n;
          return (
            <li key={label} className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
              <span className={`flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full border ${active ? "border-primary bg-primary text-primary-foreground" : done ? "border-primary/40 bg-primary/15 text-primary" : "border-border text-muted-foreground"}`}>
                {done ? <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> : n}
              </span>
              <span className={active ? "text-foreground" : "text-muted-foreground"}>{label}</span>
              {i < 3 && <span className="h-px w-4 sm:w-8 bg-border" />}
            </li>
          );
        })}
      </ol>

      <div className="mt-6 sm:mt-10 rounded-2xl border border-border bg-card p-4 sm:p-6 md:p-10">

        {/* Member discount banner — visible across every step */}
        <div className={`mb-6 rounded-xl border p-4 flex items-center justify-between gap-3 ${isAuthenticated ? "border-primary/40 bg-primary/10" : "border-dashed border-primary/40 bg-primary/5"}`}>
          <div className="flex items-center gap-3 min-w-0">
            <Sparkles className="h-5 w-5 text-primary shrink-0" />
            <div className="min-w-0">
              {isAuthenticated ? (
                <>
                  <div className="text-sm font-semibold text-primary">{t("auth.discountActive")}</div>
                  <div className="text-xs text-muted-foreground truncate">{t("auth.discountActiveSub")}</div>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold text-foreground">{t("auth.discountOffer")}</div>
                  <div className="text-xs text-muted-foreground">{t("auth.discountOfferSub")}</div>
                </>
              )}
            </div>
          </div>
          {!isAuthenticated && (
            <Link to="/login" className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90">
              {t("auth.signInCta")}
            </Link>
          )}
        </div>

        {step === 1 && (
          <div className="space-y-6">
            <div className="flex gap-2">
              {(["one-way", "round-trip"] as const).map((tt) => (
                <button
                  key={tt}
                  onClick={() => setTripType(tt)}
                  className={`rounded-full border px-5 py-2 text-sm transition-colors ${tripType === tt ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                >
                  {tt === "one-way" ? tr("bookingForm.oneWay") : tr("bookingForm.roundTrip")}
                </button>
              ))}
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <AddressInput label={t("bookingForm.pickup")} placeholder={t("bookingForm.pickupPh")} value={pickup} onChange={setPickup} allowCurrentLocation />
              <AddressInput label={t("bookingForm.dropoff")} placeholder={t("bookingForm.dropoffPh")} value={dropoff} onChange={setDropoff} />
            </div>

            <div className="grid gap-5 md:grid-cols-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{t("bookingForm.date")}</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input type="date" value={travelDate} onChange={(e) => setTravelDate(e.target.value)}
                    min={(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })()}
                    className="w-full rounded-md border border-border bg-input pl-9 pr-3 py-3 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{t("bookingForm.time")}</label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input type="time" value={travelTime} onChange={(e) => setTravelTime(e.target.value)}
                    className="w-full rounded-md border border-border bg-input pl-9 pr-3 py-3 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{t("bookingForm.passengers")}</label>
                <Stepper
                  icon={<Users className="h-4 w-4 text-muted-foreground" />}
                  value={passengers}
                  min={1}
                  max={7}
                  onChange={setPassengers}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{t("bookingForm.luggage")}</label>
                <Stepper
                  icon={<Briefcase className="h-4 w-4 text-muted-foreground" />}
                  value={luggage}
                  min={0}
                  max={10}
                  onChange={setLuggage}
                />
              </div>
            </div>

            {/* Free extras */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs uppercase tracking-[0.16em] text-muted-foreground">{t("bookingForm.extras")}</label>
                <span className="text-[11px] uppercase tracking-[0.16em] text-primary">{t("bookingForm.free")}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ExtraToggle
                  active={babySeat}
                  onToggle={() => setBabySeat((v) => !v)}
                  label={t("bookingForm.babySeat")}
                  hint={t("bookingForm.babySeatHint")}
                />
                <ExtraToggle
                  active={surfBoard}
                  onToggle={() => setSurfBoard((v) => !v)}
                  label={t("bookingForm.surfBoard")}
                  hint={t("bookingForm.surfBoardHint")}
                />
              </div>
            </div>


            {/* Live auto-estimate: appears as soon as both endpoints are picked */}
            {(pickup && dropoff) && (
              <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-background to-background p-5 md:p-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-primary">
                  <Sparkles className="h-3.5 w-3.5" /> {t("bookingForm.liveEstimate")}
                </div>
                {routeLoading && !route ? (
                  <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> {t("bookingForm.calculating")}
                  </div>
                ) : route && price ? (
                  <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
                    <div className="space-y-1 text-sm">
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                        <span><span className="text-foreground">{route.distanceKm} km</span>{route.estimated && <span className="ml-1 text-xs">(approx.)</span>}</span>
                        <span>~{route.durationMin} min</span>
                        <span>{tripType === "round-trip" ? t("bookingForm.roundTrip") : t("bookingForm.oneWay")}</span>
                        {price.nightSurcharge > 0 && <span className="text-amber-500">{t("bookingForm.nightSurcharge")}</span>}
                        {price.memberDiscount > 0 && <span className="text-primary font-semibold">−10% {t("auth.memberBadge")}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t("bookingForm.basedOn", { name: VEHICLES[vehicleType].name.toLowerCase() })}
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-auto max-w-full">
                      <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t("bookingForm.from")}</div>
                      <div className="font-display text-xl sm:text-2xl text-primary leading-tight whitespace-nowrap">{fmt(price.total)}</div>
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex justify-end">
              <button onClick={goToStep2} disabled={routeLoading}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60">
                {routeLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("bookingForm.calculatingShort")}</> : <>{t("bookingForm.continue")} <ArrowRight className="h-4 w-4" /></>}
              </button>
            </div>
          </div>
        )}

        {step === 2 && route && pickup && dropoff && (
          <div className="space-y-8">
            <RouteMap
              pickup={{ lat: pickup.lat, lng: pickup.lng }}
              dropoff={{ lat: dropoff.lat, lng: dropoff.lng }}
              pickupLabel={pickup.label}
              dropoffLabel={dropoff.label}
              polyline={route.polyline}
              className="w-full h-56 sm:h-72 rounded-2xl overflow-hidden border border-border bg-muted"
            />
            <div className="rounded-xl border border-border bg-background/40 p-4 text-sm flex flex-wrap gap-x-6 gap-y-2">
              <span><span className="text-muted-foreground">{t("bookingForm.distance")}:</span> <span className="text-primary">{route.distanceKm} km</span>{route.estimated && <span className="ml-1 text-xs text-muted-foreground">(approx.)</span>}</span>
              <span><span className="text-muted-foreground">{t("bookingForm.driving")}:</span> ~{route.durationMin} min</span>
              <span><span className="text-muted-foreground">{t("bookingForm.trip")}:</span> {tripType === "round-trip" ? t("bookingForm.roundTrip") : t("bookingForm.oneWay")}</span>
            </div>


            <div>
              <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-3">{t("bookingForm.vehicle")}</div>
              <div className="space-y-3">
                {BOOKABLE_VEHICLE_IDS.map((vid) => {
                  const v = VEHICLES[vid];
                  const active = vehicleType === vid;
                  const p = estimatePrice({ vehicleType: vid, distanceKm: route.distanceKm, tripType, timeHHmm: travelTime, memberDiscount: isAuthenticated });
                  return (
                    <button
                      key={vid}
                      onClick={() => setVehicleType(vid)}
                      className={`w-full text-left bg-white ring-1 transition-all overflow-hidden rounded-3xl ${active ? "ring-2 ring-primary shadow-md" : "ring-border hover:ring-primary/40 hover:shadow-sm"}`}
                    >
                      <div className="flex items-center gap-3 p-4">
                        <div className="flex items-center justify-center flex-shrink-0 w-28 sm:w-32 aspect-[4/3] rounded-2xl bg-gradient-to-br from-secondary/40 to-background">
                          <img
                            src={v.image}
                            alt={v.name}
                            loading="lazy"
                            className="max-h-full max-w-full object-contain p-1"
                          />
                        </div>



                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="text-sm font-bold text-foreground leading-tight">{v.name}</h3>
                            <div className="text-sm font-bold text-foreground whitespace-nowrap">{fmt(p.total)}</div>
                          </div>

                          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-muted-foreground">
                            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {v.capacity}</span>
                            <span className="inline-flex items-center gap-1"><Briefcase className="h-3 w-3" /> {v.luggage}</span>
                            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {route.distanceKm} km</span>
                            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {route.durationMin} min</span>
                          </div>

                          <div className="space-y-1.5">
                            <div className="inline-flex w-full items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-medium text-emerald-700">
                              <Clock className="h-3 w-3" /> {t("bookingForm.freeWait")}
                            </div>
                            <div className="inline-flex w-full items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-medium text-emerald-700">
                              <Check className="h-3 w-3" /> {t("bookingForm.freeCancel")}
                            </div>
                          </div>
                        </div>

                      </div>

                    </button>
                  );
                })}
              </div>
            </div>






            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm hover:bg-muted">
                <ArrowLeft className="h-4 w-4" /> {t("bookingForm.back")}
              </button>
              <button onClick={goToStep3} className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
                {t("bookingForm.continue")} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="grid gap-5 md:grid-cols-2">
              <Field label={t("bookingForm.fullName")} value={details.customer_name} onChange={(v) => setDetails({...details, customer_name: v})} error={detailsErrors.customer_name} />
              <Field label={t("bookingForm.email")} type="email" value={details.customer_email} onChange={(v) => setDetails({...details, customer_email: v})} error={detailsErrors.customer_email} />
              <PhoneField label={t("bookingForm.phone")} value={details.customer_phone} onChange={(v) => setDetails({...details, customer_phone: v})} error={detailsErrors.customer_phone} />
              <Field label={t("bookingForm.flightNum")} placeholder="AT201" icon={<Plane className="h-4 w-4" />} value={details.flight_number} onChange={(v) => setDetails({...details, flight_number: v})} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{t("bookingForm.notes")}</label>
              <textarea value={details.notes} onChange={(e) => setDetails({...details, notes: e.target.value})}
                rows={3} maxLength={500}
                placeholder={t("bookingForm.notesPh")}
                className="w-full rounded-md border border-border bg-input px-3 py-3 text-sm focus:border-primary focus:outline-none" />
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm hover:bg-muted">
                <ArrowLeft className="h-4 w-4" /> {t("bookingForm.back")}
              </button>
              <button onClick={goToStep4} className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
                {t("bookingForm.review")} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 4 && route && price && pickup && dropoff && (
          <div className="space-y-8">
            <RouteMap
              pickup={{ lat: pickup.lat, lng: pickup.lng }}
              dropoff={{ lat: dropoff.lat, lng: dropoff.lng }}
              pickupLabel={pickup.label}
              dropoffLabel={dropoff.label}
              polyline={route.polyline}
              className="w-full h-56 sm:h-72 rounded-2xl overflow-hidden border border-border bg-muted"
            />
            <div className="grid gap-6 md:grid-cols-2">
              <Summary title={t("bookingForm.sumTrip")}>
                <Row k="Type" v={tripType === "round-trip" ? t("bookingForm.roundTrip") : t("bookingForm.oneWay")} />
                <Row k={t("bookingForm.pickupK")} v={pickup.label} />
                <Row k={t("bookingForm.dropoffK")} v={dropoff.label} />
                <Row k={t("bookingForm.whenK")} v={`${travelDate} · ${travelTime}`} />
                <Row k={t("bookingForm.distanceK")} v={`${route.distanceKm} km (~${route.durationMin} min)`} />
                <Row k={t("bookingForm.paxK")} v={t("bookingForm.paxLuggage", { p: passengers, l: luggage })} />
                {(babySeat || surfBoard) && (
                  <Row k={t("bookingForm.extrasK")} v={[babySeat && t("bookingForm.babySeat"), surfBoard && t("bookingForm.surfBoard")].filter(Boolean).join(", ") + t("bookingForm.extraSuffix")} />
                )}
              </Summary>
              <Summary title={t("bookingForm.sumVeh")}>
                <Row k={t("bookingForm.vehicleK")} v={VEHICLES[vehicleType].name} />
                <Row k={t("bookingForm.nameK")} v={details.customer_name} />
                <Row k={t("bookingForm.phoneK")} v={details.customer_phone} />
                <Row k={t("bookingForm.emailK")} v={details.customer_email} />
                {details.flight_number && <Row k={t("bookingForm.flightK")} v={details.flight_number} />}
                {details.notes && <Row k={t("bookingForm.notesK")} v={details.notes} />}
              </Summary>
            </div>

            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 sm:p-6 flex flex-wrap justify-between items-center gap-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-[0.18em] text-primary">{t("bookingForm.estimatedPrice")}</div>
                <div className="text-xs text-muted-foreground mt-1">{t("bookingForm.finalNote")} · {price.nightSurcharge > 0 ? t("bookingForm.nightSurcharge") : t("bookingForm.noSurcharge")}</div>
              </div>
              <div className="font-display text-xl sm:text-2xl text-primary whitespace-nowrap ml-auto shrink-0">{fmt(price.total)}</div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-primary mb-3">{t("bookingForm.howConfirm")}</div>
              <div className="grid gap-3 md:grid-cols-2">
                {([
                  { id: "cash" as const, icon: <MessageCircle className="h-5 w-5" />, title: t("bookingForm.cashTitle"), desc: t("bookingForm.cashDesc") },
                  { id: "online" as const, icon: <CreditCard className="h-5 w-5" />, title: t("bookingForm.onlineTitle"), desc: t("bookingForm.onlineDesc") },
                ]).map((opt) => {
                  const active = paymentMethod === opt.id;
                  return (
                    <button key={opt.id} type="button" onClick={() => setPaymentMethod(opt.id)}
                      className={`text-left rounded-xl border p-5 transition-all ${active ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-border hover:border-primary/40"}`}>
                      <div className="flex items-center gap-3">
                        <span className={`flex h-9 w-9 items-center justify-center rounded-full ${active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>{opt.icon}</span>
                        <div className="font-medium">{opt.title}</div>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">{opt.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-end">
              <button onClick={() => setStep(3)} className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm hover:bg-muted">
                <ArrowLeft className="h-4 w-4" /> {t("bookingForm.back")}
              </button>
              {paymentMethod === "online" ? (
                <div className="flex flex-col items-stretch sm:items-end gap-2">
                  <button
                    type="button"
                    disabled
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-muted px-8 py-3.5 text-sm font-medium text-muted-foreground cursor-not-allowed"
                  >
                    {t("bookingForm.onlineNA")}
                  </button>
                  <p className="text-xs text-muted-foreground sm:text-right">
                    {t("bookingForm.onlineNote")}
                  </p>
                </div>
              ) : (
                <button onClick={submit} disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60">
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("bookingForm.sending")}</> : <>{t("bookingForm.confirmCta")} <ArrowRight className="h-4 w-4" /></>}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, icon, error }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string; icon?: React.ReactNode; error?: string;
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{label}</label>
      <div className="relative">
        {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{icon}</span>}
        <input type={type} value={value} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full rounded-md border bg-input ${icon ? "pl-9" : "pl-3"} pr-3 py-3 text-sm focus:outline-none ${error ? "border-destructive" : "border-border focus:border-primary"}`} />
      </div>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}

function Summary({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border p-5">
      <div className="text-xs uppercase tracking-[0.18em] text-primary mb-3">{title}</div>
      <dl className="space-y-2 text-sm">{children}</dl>
    </div>
  );
}
function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground shrink-0">{k}</dt>
      <dd className="text-right text-foreground">{v}</dd>
    </div>
  );
}

function Stepper({
  value, min, max, onChange, icon,
}: { value: number; min: number; max: number; onChange: (n: number) => void; icon?: React.ReactNode }) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));
  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-input px-2 py-1.5">
      <button
        type="button"
        onClick={dec}
        disabled={value <= min}
        aria-label="Decrease"
        className="flex h-9 w-9 items-center justify-center rounded-full text-foreground hover:bg-muted disabled:opacity-40"
      >
        −
      </button>
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        {icon}
        <span className="tabular-nums">{value}</span>
      </div>
      <button
        type="button"
        onClick={inc}
        disabled={value >= max}
        aria-label="Increase"
        className="flex h-9 w-9 items-center justify-center rounded-full text-foreground hover:bg-muted disabled:opacity-40"
      >
        +
      </button>
    </div>
  );
}

function ExtraToggle({
  active, onToggle, label, hint,
}: { active: boolean; onToggle: () => void; label: string; hint?: string }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2.5 text-left text-sm transition ${
        active
          ? "border-primary bg-primary/5 text-foreground"
          : "border-border bg-input text-muted-foreground hover:border-primary/50 hover:text-foreground"
      }`}
    >
      <span>
        <span className="block font-medium text-foreground">{label}</span>
        {hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
      </span>
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full border ${
          active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"
        }`}
      >
        {active ? "✓" : ""}
      </span>
    </button>
  );
}

function ConfirmingOverlay() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white bg-gradient-to-b from-white via-primary/5 to-primary/10 px-6 py-10"
    >
      <img src={logo} alt="Bacha Way Travel" className="h-16 w-auto mb-8 animate-pulse" />
      <h1 className="font-display text-3xl sm:text-4xl text-foreground text-center max-w-md leading-tight">
        You are almost there<span className="text-primary">…</span>
      </h1>
      <p className="mt-3 text-base text-muted-foreground text-center max-w-sm">
        Confirming your booking with Bacha Way Travel
      </p>
      <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
        Sending your confirmation
      </div>
    </div>
  );
}
